import { Avatar, Button, Flex, message, Skeleton } from "antd";
import { useRef } from "react";
import { useUploadFileMutation } from "../../../../app/features/auth/auth.api";
import { useUpdateEmployeeMutation } from "../../../../app/features/employee/employee.api";
import type { IEmployee } from "../../../../app/features/employee/employee.types";
import IMAGES from "../../../../assets/images";
import { ENV_CONFIG } from "../../../../config/global.config";
import { cn } from "../../../../utils/global.utils";
import CameraIcon from "../_icons/CameraIcon";

type Props = {
  data?: IEmployee;
  loading?: boolean;
};

export default function ProfileHeader({ data, loading }: Props) {
  const [uploadFile, { isLoading: isUploading }] = useUploadFileMutation();
  const [updateEmployee, { isLoading: isUpdating }] =
    useUpdateEmployeeMutation();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check file size (e.g., 5MB)
    if (file.size > 5 * 1024 * 1024) {
      message.error("ছবির আকার 5MB এর বেশি হতে পারেনা");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    try {
      const uploadRes = await uploadFile(formData).unwrap();
      const photoUrl = uploadRes.file.url;

      if (data?.id) {
        await updateEmployee({
          id: data.id,
          data: { profilePhotoUrl: `${ENV_CONFIG.IMAGE_URL}/${photoUrl}` },
        }).unwrap();
        message.success("প্রোফাইল ছবি সফলভাবে আপডেট হয়েছে");
      }
    } catch (error) {
      message.error("প্রোফাইল ছবি আপডেট করতে ব্যর্থ হয়েছে");
      console.error(error);
    } finally {
      // Reset input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  if (loading) {
    return (
      <Flex align="center" gap={16}>
        <Skeleton.Avatar active size={96} shape="circle" />
        <div className="flex-1 flex flex-col">
          <Skeleton.Input
            active
            size="small"
            style={{ width: 100, marginBottom: 8 }}
          />
          <Skeleton.Input
            active
            size="small"
            style={{ width: 200, marginBottom: 8, display: "block" }}
          />
          <Skeleton.Button
            active
            size="default"
            shape="round"
            style={{ width: 150 }}
          />
        </div>
      </Flex>
    );
  }

  return (
    <Flex align="center" gap={16}>
      <Avatar size={96} src={data?.profilePhotoUrl || IMAGES.avatar} />
      <div className="flex-1">
        <h6 className="text-theme-zinc-700 text-lg font-medium">
          প্রোফাইল ফটো
        </h6>
        <p className="text-theme-slate-500 text-sm font-normal mt-1 mb-2 ">
          {/* PNG or JPG no bigger than 800px width and height */}
          <span className="font-roboto">PNG</span> বা{" "}
          <span className="font-roboto">JPG</span> ফাইল আপলোড করুন। যেখানে
          প্রস্থ ও উচ্চতা ৮০০ পিক্সেলের বেশি নয়।
        </p>
        <input
          type="file"
          ref={fileInputRef}
          className="hidden"
          accept=".png,.jpg,.jpeg"
          onChange={handleFileChange}
        />
        <Button
          variant="outlined"
          type="text"
          size="middle"
          loading={isUploading || isUpdating}
          className={cn(
            "rounded-full",
            "!text-theme-primary !bg-[#E9F2F2] hover:!bg-[#d3e6e6]"
          )}
          icon={<CameraIcon />}
          onClick={handleButtonClick}
        >
          ফটো পরিবর্তন করুন
        </Button>
      </div>
    </Flex>
  );
}
